import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { RootLayout } from "@/components/RootLayout";
import { AppShell } from "@/components/AppShell";
import { PortalShell } from "@/components/PortalShell";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { SecurityOnboardingGate } from "@/auth/SecurityOnboardingGate";
import { SplashPage } from "@/pages/SplashPage";
import { LoginPage } from "@/pages/LoginPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { TermsPage } from "@/pages/TermsPage";
import { PortalPage } from "@/pages/PortalPage";
import { DirectMarketsPage } from "@/pages/DirectMarketsPage";
import { ConstructionBondPage } from "@/pages/ConstructionBondPage";
import { CommercialBondPage } from "@/pages/CommercialBondPage";
import { NewBondSubmissionPage } from "@/pages/NewBondSubmissionPage";
import { BondSubmissionsPage } from "@/pages/BondSubmissionsPage";
import { SuretyBondsPage } from "@/pages/SuretyBondsPage";
import { SuretySubmissionPage } from "@/pages/SuretySubmissionPage";
import { QuotesPage } from "@/pages/QuotesPage";
import { BindersPage } from "@/pages/BindersPage";
import { PoliciesPage } from "@/pages/PoliciesPage";
import { DeclinationsPage } from "@/pages/DeclinationsPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { SecurityPage } from "@/pages/SecurityPage";
import { AccountsListPage } from "@/pages/AccountsListPage";
import { AddAccountPage } from "@/pages/AddAccountPage";
import { SuretyAccountPage } from "@/pages/SuretyAccountPage";
import { SubmissionsPage } from "@/pages/SubmissionsPage";
import { NewSubmissionPage } from "@/pages/NewSubmissionPage";
import { SubmissionDetailPage } from "@/pages/SubmissionDetailPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { NotificationPreferencesPage } from "@/pages/NotificationPreferencesPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

// The account record page is the first MUI screen, so it loads on demand: its
// components (tabs, menus, dialogs) stay out of the first download of every
// other page, login included.
const AccountDetailPage = lazy(() =>
  import("@/pages/AccountDetailPage").then((m) => ({
    default: m.AccountDetailPage,
  })),
);

// The app's route table, factored out of <App /> so tests can mount it inside a
// MemoryRouter at an arbitrary path. RootLayout is the chromeless outer layout
// (it owns the document.title mapping). Under it, the public surface (splash,
// auth flows, and the in-app 404 so unknown deep links resolve in-app under
// static hosting) renders in the marketing AppShell; the protected group renders
// in the authenticated PortalShell (left nav + account menu, BRK-S035). PortalShell
// wraps the security-onboarding gate so an authenticated broker always sees the
// shell chrome and the gate only swaps the page content.
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route element={<AppShell />}>
          <Route index element={<SplashPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          {/* Legal (BRK-S149) — public on purpose: a producer must be able to
              read the terms before signing in, and a reviewer without an
              account must be able to read them at all. */}
          <Route path="terms" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route element={<PortalShell />}>
            <Route element={<SecurityOnboardingGate />}>
              <Route path="portal" element={<PortalPage />} />
              <Route path="direct-markets" element={<DirectMarketsPage />} />
              {/* Surety platform: the two bond exchanges are wired; the other
                  Surety tabs are inert placeholders. */}
              <Route
                path="surety/construction"
                element={<ConstructionBondPage />}
              />
              <Route
                path="surety/commercial"
                element={<CommercialBondPage />}
              />
              {/* The Surety platform's own view of the SAME own-scoped
                  accounts (BRK-S138): the list is the shared page with a
                  different destination, the detail is the bond profile. */}
              <Route
                path="surety/accounts"
                element={<AccountsListPage basePath="/surety/accounts" />}
              />
              <Route
                path="surety/accounts/:id"
                element={<SuretyAccountPage />}
              />
              <Route
                path="surety/submissions"
                element={<BondSubmissionsPage />}
              />
              {/* ISSUED bonds, brokerage-wide (BRK-S181) — the nav's Bonds.
                  Not the orders list above, which is its Submissions. */}
              <Route path="surety/bonds" element={<SuretyBondsPage />} />
              <Route
                path="surety/submissions/new"
                element={<NewBondSubmissionPage />}
              />
              {/* The same flow, resumed from the server (BRK-S088). It moved
                  one segment down in BRK-S140 so `/:id` could become the
                  read-first PROFILE: a filed order is a record, not a form, and
                  a broker checking what they submitted should not have to walk
                  a stepper to read it. */}
              <Route
                path="surety/submissions/:id/continue"
                element={<NewBondSubmissionPage />}
              />
              <Route
                path="surety/submissions/:id"
                element={<SuretySubmissionPage />}
              />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route
                path="preferences"
                element={<NotificationPreferencesPage />}
              />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="accounts" element={<AccountsListPage />} />
              <Route path="accounts/new" element={<AddAccountPage />} />
              <Route
                path="accounts/:id"
                element={
                  <Suspense
                    fallback={
                      <p
                        role="status"
                        className="py-16 text-center text-brk-dim"
                      >
                        Loading account…
                      </p>
                    }
                  >
                    <AccountDetailPage />
                  </Suspense>
                }
              />
              <Route path="submissions" element={<SubmissionsPage />} />
              <Route path="quotes" element={<QuotesPage />} />
              <Route path="binders" element={<BindersPage />} />
              <Route path="policies" element={<PoliciesPage />} />
              <Route path="declinations" element={<DeclinationsPage />} />
              <Route path="submissions/new" element={<NewSubmissionPage />} />
              <Route
                path="submissions/:id"
                element={<SubmissionDetailPage />}
              />
            </Route>
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
